import { appScheme } from './appConstants';

/**
 * Deep-Link-Katalog für die Admin-Editoren (Announcements, Broadcasts).
 *
 * Warum es diese Datei gibt: beide Editoren haben Ziel-URLs vorgeschlagen, die
 * es nicht gab — Schema `currivium` (das keine App registriert) auf Pfaden wie
 * `lesson/<id>`, `curriculum` und `settings`, wobei eine Route `settings`
 * ohnehin nicht existiert. Beide Consumer verwerfen Unbekanntes STILL: kein
 * Fehler, kein Toast, kein Log auf dem Gerät. Ein solcher CTA sieht im Admin
 * fertig aus und tut nichts.
 *
 * Es sind ZWEI Consumer mit zwei verschiedenen Pfad-Mengen — die Listen unten
 * sind je aus ihrer Quelle abgeschrieben, nicht voneinander:
 *
 *  1. Announcement-CTA → `AnnouncementSheet` ruft `Linking.canOpenURL` /
 *     `openURL`; aufgelöst wird gegen die React-Navigation-Linking-Config in
 *     `packages/core/src/app/linking.ts` (`linkingBase.config.screens`).
 *  2. Broadcast-Push → `parseDeepLink` in
 *     `packages/core/src/hooks/useNotifications.ts` parst selbst und navigiert
 *     per `navigationRef`. Die Linking-Config ist daran unbeteiligt.
 *
 * Die Mengen decken sich in KEINE Richtung: nur der Push kennt
 * `learn/Document/<id>` und `quiz/FlashcardDecks`; nur der CTA kennt `plan`,
 * `plan/termine`, `plan/termine/<id>`, `plan/lernweg`, `learn/Topic/<id>`,
 * `learn/glossary/<id>`, `quiz`, `profile` und `profile/Subscription`. Wer
 * hier etwas ergänzt, sieht zuerst in der jeweiligen Quelle nach.
 *
 * Optionale Segmente (`plan/termine/:eventId?`) kann `matches()` nicht — ein
 * solcher Pfad steht hier als ZWEI Einträge, mit und ohne das Segment.
 *
 * Das Schema kommt pro App aus `appScheme()` — beide Consumer bilden ihren
 * Prefix zur Laufzeit aus `appConfig.tenant.scheme`, sind also nicht auf
 * `curalearn://` festgelegt.
 */

export interface DeepLinkTarget {
  /**
   * Prüfform. `:` als Segment steht für einen beliebigen nicht-leeren
   * Pfadabschnitt (eine ID).
   */
  pattern: string;
  /** Anzeigeform mit sprechendem Platzhalter. */
  display: string;
  /** Menschenlesbares Ziel für die Beispiel-Liste. */
  label: string;
  /** In der Beispiel-Liste der Editoren zeigen. */
  example?: boolean;
}

/**
 * Ziele des Announcement-CTA — vollständig aus `linkingBase.config.screens`
 * (`packages/core/src/app/linking.ts`).
 */
export const ANNOUNCEMENT_TARGETS: readonly DeepLinkTarget[] = [
  { pattern: 'home', display: 'home', label: 'Startseite' },
  // Plan-Tab (CUR-958). Der Push kennt keinen dieser Pfade — `parseDeepLink`
  // hat keinen Zweig dafür, das Backend verschickt sie also auch nicht.
  { pattern: 'plan', display: 'plan', label: 'Dein Weg' },
  { pattern: 'plan/termine', display: 'plan/termine', label: 'Termine' },
  // Optionales `:eventId?` (CUR-1486) — `date` darf zusätzlich als Query
  // mitreisen, die Prüfung schneidet sie ab.
  { pattern: 'plan/termine/:', display: 'plan/termine/<eventId>', label: 'Termin' },
  // Lernweg (CUR-1596).
  { pattern: 'plan/lernweg', display: 'plan/lernweg', label: 'Lernweg' },
  { pattern: 'learn/Curriculum', display: 'learn/Curriculum', label: 'Lehrplan', example: true },
  {
    pattern: 'learn/Lesson/:',
    display: 'learn/Lesson/<lessonId>',
    label: 'Lektion',
    example: true,
  },
  { pattern: 'learn/Topic/:', display: 'learn/Topic/<topicId>', label: 'Thema' },
  { pattern: 'learn/glossary', display: 'learn/glossary', label: 'Glossar' },
  { pattern: 'learn/glossary/:', display: 'learn/glossary/<entryId>', label: 'Glossareintrag' },
  { pattern: 'quiz', display: 'quiz', label: 'Quiz-Center', example: true },
  { pattern: 'quiz/SpacedRep', display: 'quiz/SpacedRep', label: 'Wiederholung' },
  { pattern: 'quiz/ExamSim', display: 'quiz/ExamSim', label: 'Prüfungssimulation' },
  { pattern: 'profile', display: 'profile', label: 'Profil' },
  { pattern: 'profile/Statistics', display: 'profile/Statistics', label: 'Statistik' },
  { pattern: 'profile/Subscription', display: 'profile/Subscription', label: 'Abo', example: true },
  {
    pattern: 'profile/Feedback',
    display: 'profile/Feedback?trigger=<kampagne>',
    label: 'Feedback',
  },
  { pattern: 'profile/SupportTickets', display: 'profile/SupportTickets', label: 'Meine Anfragen & Antworten' },
  { pattern: 'profile/SupportTicket/:', display: 'profile/SupportTicket/<ticketId>', label: 'Anfrage & Antwort' },
  // E-Mail-Opt-in-Kampagne (CUR-891): CTA auf die Benachrichtigungs-
  // Einstellungen mit dem E-Mail-Updates-Toggle.
  {
    pattern: 'profile/NotificationSettings',
    display: 'profile/NotificationSettings',
    label: 'Benachrichtigungs-Einstellungen',
    example: true,
  },
  // Onboarding-Routen sind als CTA sinnlos (Announcements sehen nur
  // eingeloggte Nutzer), aber die Linking-Config kennt sie — sie stehen hier,
  // damit die Pfad-Prüfung sie nicht faelschlich als unbekannt meldet.
  { pattern: 'onboarding/Welcome', display: 'onboarding/Welcome', label: 'Onboarding' },
  { pattern: 'onboarding/Auth', display: 'onboarding/Auth', label: 'Onboarding' },
  {
    pattern: 'onboarding/EmailVerification',
    display: 'onboarding/EmailVerification',
    label: 'Onboarding',
  },
  { pattern: 'onboarding/PrepPath', display: 'onboarding/PrepPath', label: 'Onboarding' },
  {
    pattern: 'onboarding/PriorKnowledge',
    display: 'onboarding/PriorKnowledge',
    label: 'Onboarding',
  },
  { pattern: 'onboarding/Rhythm', display: 'onboarding/Rhythm', label: 'Onboarding' },
  { pattern: 'onboarding/Goal', display: 'onboarding/Goal', label: 'Onboarding' },
];

/**
 * Ziele des Broadcast-Push — vollständig aus `parseDeepLink`
 * (`packages/core/src/hooks/useNotifications.ts`).
 */
export const PUSH_TARGETS: readonly DeepLinkTarget[] = [
  { pattern: 'home', display: 'home', label: 'Startseite', example: true },
  { pattern: 'learn/Curriculum', display: 'learn/Curriculum', label: 'Lehrplan' },
  {
    pattern: 'learn/Lesson/:',
    display: 'learn/Lesson/<lessonId>',
    label: 'Lektion',
    example: true,
  },
  { pattern: 'learn/Document/:', display: 'learn/Document/<documentId>', label: 'Dokument' },
  { pattern: 'learn/glossary', display: 'learn/glossary', label: 'Glossar' },
  {
    pattern: 'quiz/SpacedRep',
    display: 'quiz/SpacedRep',
    label: 'Wiederholung',
    example: true,
  },
  { pattern: 'quiz/FlashcardDecks', display: 'quiz/FlashcardDecks', label: 'Karteikarten' },
  { pattern: 'quiz/ExamSim', display: 'quiz/ExamSim', label: 'Prüfungssimulation' },
  { pattern: 'profile/Statistics', display: 'profile/Statistics', label: 'Statistik' },
  // display trägt die Query mit, weil die Kampagnen-Attribution allein am
  // trigger hängt — ein kopiertes Beispiel ohne ?trigger wäre technisch
  // gültig und lieferte still unzuordenbares Feedback (CUR-857).
  {
    pattern: 'profile/Feedback',
    display: 'profile/Feedback?trigger=<kampagne>',
    label: 'Feedback',
    example: true,
  },
  {
    pattern: 'profile/NotificationSettings',
    display: 'profile/NotificationSettings',
    label: 'Benachrichtigungs-Einstellungen',
  },
];

/** `curalearn://` bzw. `currivium-hpp://` — der Prefix der gewählten App. */
export function schemePrefix(app: string): string {
  return `${appScheme(app)}://`;
}

/** Trägt die URL das Schema der gewählten App? */
export function isAppDeepLink(url: string, app: string): boolean {
  return url.startsWith(schemePrefix(app));
}

function matches(target: DeepLinkTarget, path: string): boolean {
  const want = target.pattern.split('/');
  const got = path.split('/');
  if (want.length !== got.length) return false;
  return want.every((seg, i) => (seg === ':' ? got[i]!.length > 0 : seg === got[i]));
}

/**
 * Löst der Pfad hinter dem Schema auf eine echte Route auf?
 *
 * Query und Fragment werden abgeschnitten — die Consumer matchen beide auf dem
 * reinen Pfad, ein `?utm_source=…` macht den Link also nicht ungültig.
 */
export function isKnownDeepLinkPath(
  url: string,
  app: string,
  targets: readonly DeepLinkTarget[],
): boolean {
  if (!isAppDeepLink(url, app)) return false;
  const path = url.slice(schemePrefix(app).length).split(/[?#]/)[0]!.replace(/\/+$/, '');
  return targets.some((t) => matches(t, path));
}

/** Vollständige Beispiel-URLs für die Hinweiszeile der Editoren. */
export function deepLinkExamples(app: string, targets: readonly DeepLinkTarget[]): string[] {
  return targets.filter((t) => t.example).map((t) => `${schemePrefix(app)}${t.display}`);
}

/** Alle gültigen Pfade als Aufzählung — für die Fehlermeldung bei Unbekanntem. */
export function deepLinkPathList(targets: readonly DeepLinkTarget[]): string {
  return targets
    .filter((t) => !t.pattern.startsWith('onboarding/'))
    .map((t) => t.display)
    .join(', ');
}
